/**
 * AdminPodiumRulesPage.jsx — Admin-configurable Podium Points rules.
 *
 * Lets super_admin / org_admin / event_manager tune the per-action
 * point values, daily caps, weekly caps, and active flag.  Never
 * touches Diploks — these rules govern only the FREE Podium Points
 * engagement layer.
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  adminListRules, adminUpdateRule, formatApiError,
} from "../../lib/gamificationApi";
import { toast } from "sonner";
import {
  Settings, Save, ShieldCheck, AlertCircle,
} from "lucide-react";

export default function AdminPodiumRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const d = await adminListRules();
      setRules(d.rules || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const updateRule = (idx, patch) => {
    const next = [...rules];
    next[idx] = { ...next[idx], ...patch };
    setRules(next);
  };

  const saveRule = async (r) => {
    setSaving(r.action_key);
    try {
      await adminUpdateRule({
        action_key: r.action_key,
        points: parseInt(r.points, 10),
        daily_cap: parseInt(r.daily_cap, 10),
        weekly_cap: parseInt(r.weekly_cap, 10),
        active: !!r.active,
      });
      toast.success(`Saved · ${r.label}`, { id: `podium-rule-save-toast-${r.action_key}` });
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  return (
    <DashboardLayout title="Podium Points · Rules">
      <div data-testid="admin-podium-rules" className="space-y-6 pb-12">
        <section className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white">
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Settings size={12} /> Admin · Diplomexa Academy
            </p>
            <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
              Podium Points Rules
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-blue-100">
              Tune the FREE engagement layer. Per-rule caps + a 1,000-point
              daily ceiling per delegate keep abuse in check.
              <span className="block mt-1 text-[#D4AF37] font-semibold">
                These rules never affect Diploks — they govern only Podium Points.
              </span>
            </p>
          </div>
        </section>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
          <p className="text-[11px] text-emerald-800 flex items-start gap-2">
            <ShieldCheck size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>Diploks vs Podium Points separation:</strong>{" "}
              Podium Points are awarded for free actions (lessons completed,
              live sessions attended, certificates earned).  Diploks remain
              the paid platform credit and are never affected by these rules.
              No discounts, no conversions — by design.
            </span>
          </p>
        </div>

        {loading ? (
          <div className="grid h-48 place-items-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-3">
            {rules.map((r, i) => (
              <div key={r.action_key}
                    data-testid={`admin-podium-rule-${r.action_key}`}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F4C81]">
                      {r.action_key}
                    </p>
                    <p className="text-sm font-semibold text-[#0A192F]">{r.label}</p>
                  </div>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!r.active}
                      onChange={(e) => updateRule(i, { active: e.target.checked })}
                      data-testid={`admin-podium-rule-${r.action_key}-active`}
                    />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">
                      Active
                    </span>
                  </label>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Field label="Points awarded">
                    <input
                      type="number" min={0} max={10000}
                      value={r.points}
                      onChange={(e) => updateRule(i, { points: e.target.value })}
                      data-testid={`admin-podium-rule-${r.action_key}-points`}
                      className={FIELD}
                    />
                  </Field>
                  <Field label="Daily cap (per user)">
                    <input
                      type="number" min={0} max={100000}
                      value={r.daily_cap}
                      onChange={(e) => updateRule(i, { daily_cap: e.target.value })}
                      data-testid={`admin-podium-rule-${r.action_key}-daily`}
                      className={FIELD}
                    />
                  </Field>
                  <Field label="Weekly cap (per user)">
                    <input
                      type="number" min={0} max={1000000}
                      value={r.weekly_cap}
                      onChange={(e) => updateRule(i, { weekly_cap: e.target.value })}
                      data-testid={`admin-podium-rule-${r.action_key}-weekly`}
                      className={FIELD}
                    />
                  </Field>
                </div>
                <button
                  type="button"
                  onClick={() => saveRule(r)}
                  disabled={saving === r.action_key}
                  data-testid={`admin-podium-rule-${r.action_key}-save`}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#0A192F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50] disabled:opacity-50"
                >
                  <Save size={11} /> {saving === r.action_key ? "Saving…" : "Save rule"}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <p className="text-[11px] text-slate-600 flex items-start gap-2">
            <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>Anti-abuse:</strong> beyond per-rule caps, each delegate
              has a hard <strong>1,000 points/day</strong> ceiling. Idempotency
              keys de-duplicate accidental retries from the LMS hooks.
            </span>
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

const FIELD = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81]/15";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
